import _LessonTodayCardStartMutation from "../__generated__/LessonTodayCardStartMutation.graphql";
import _LessonTodayCardQuery from "../__generated__/LessonTodayCardQuery.graphql";
import { useState } from 'react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Play } from 'lucide-react';
import { En } from '../i18n/En';
import { t } from '../i18n/t';
import { localDay } from '../lessons/localDay.ts';
import { nextStep } from '../lessons/progress.ts';
import { shouldOfferNextSet, todayChecklist } from '../lessons/today.ts';
import { registerFor } from '../profile/register';
import { DaySegments, KindTile } from './LessonProgress';
import { kindLabel } from './lessonLabels.ts';
import type { LessonTodayCardQuery as LessonTodayCardQueryType } from '../__generated__/LessonTodayCardQuery.graphql';
import type { LessonTodayCardStartMutation } from '../__generated__/LessonTodayCardStartMutation.graphql';
const LessonTodayCardQuery = (_LessonTodayCardQuery.hash && _LessonTodayCardQuery.hash !== "8dbdaf8cd64c7b62205717f85e2aa30c" && console.error("The definition of 'LessonTodayCardQuery' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonTodayCardQuery);
const StartNext = (_LessonTodayCardStartMutation.hash && _LessonTodayCardStartMutation.hash !== "8dbc225c5c6f728ff673401fe9efd6ca" && console.error("The definition of 'LessonTodayCardStartMutation' appears to have changed. Run `relay-compiler` to update the generated files to receive the expected data."), _LessonTodayCardStartMutation);

/**
 * Today's list for the set being followed (lesson library PRD §4.2): the
 * course's order, a filled tile once a lesson was heard through today, the
 * week as seven segments, one button to the first step not yet done, and
 * from day 8 an offer of the next set beside "stay". Renders nothing for the kid
 * register or when no set is active, so the home screen is unchanged for
 * anyone the course is not for.
 */
export function LessonTodayCard({
  fetchKey = 0
}: {
  fetchKey?: number;
}) {
  const today = localDay(new Date());
  const data = useLazyLoadQuery<LessonTodayCardQueryType>(LessonTodayCardQuery, {
    localDay: today
  }, {
    fetchPolicy: 'store-and-network',
    fetchKey
  });
  const [start, starting] = useMutation<LessonTodayCardStartMutation>(StartNext);
  const navigate = useNavigate();
  const [stayed, setStayed] = useState(false);
  const learner = data.me?.learner;
  const set = learner?.activeLessonSet;
  if (!learner || !set || registerFor(learner.defaultCharacter?.audience) === 'kid') return null;
  const rows = todayChecklist(set.lessons, today);
  // The next set is the next by position, not the next in this list: the
  // course's order is the server's `position` (PRD §4.2).
  const next = [...learner.lessonSets].sort((a, b) => a.position - b.position).find(s => s.position > set.position);
  const offer = !stayed && next !== undefined && shouldOfferNextSet(set.day ?? null);
  const step = nextStep(rows);
  const playedToday = rows.some(r => r.playsToday > 0);
  const stepName = (r: {
    kind: string;
    part?: string | null;
  }) => `${t(kindLabel(r.kind))}${r.part ? ` ${r.part}` : ''}`;
  return <section className="bg-base-100 border-primary/30 flex flex-col gap-4 rounded-box border-2 p-4 shadow-sm">
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-primary text-xs font-semibold tracking-wide uppercase">
            {t('lessons-this-week')}
            {set.level != null && <> · {t('lessons-level', {
              level: set.level
            })}</>}
          </span>
          {set.day != null && <span className="text-base-content/60 text-sm tabular-nums">
              {t('lessons-day-of', {
            day: set.day,
            total: 7
          })}
            </span>}
        </div>
        <Link to={`/lessons/${encodeURIComponent(set.id)}`} className="flex min-h-(--hit-min) items-center gap-1 text-xl font-bold">
          <En>{set.name}</En>
          <ChevronRight aria-hidden className="text-base-content/40 size-5" />
        </Link>
        {set.day != null && <DaySegments day={Math.min(set.day, 7)} playedToday={playedToday} />}
      </div>
      <ul className="flex flex-col">
        {rows.map(r => <li key={r.lessonId}>
            <Link to={`/lessons/play/${encodeURIComponent(r.lessonId)}`} aria-current={step?.lessonId === r.lessonId ? 'step' : undefined} className={`hover:bg-base-200 -mx-2 flex min-h-(--hit-min) items-center gap-3 rounded-xl px-2 py-1 ${step?.lessonId === r.lessonId ? 'bg-primary/5' : ''}`}>
              <KindTile kind={r.kind} done={r.done} />
              <span className={r.done ? 'text-base-content/60' : 'font-medium'}>{stepName(r)}</span>
              {r.done && <span className="sr-only">{r.kind === 'ARTICLE' ? t('lessons-read-today') : t('lessons-done')}</span>}
              {r.kind === 'MINI_STORY' && r.playsToday > 0 && <span className="badge badge-ghost badge-sm ms-auto">
                  {t('lessons-heard-times', {
              count: r.playsToday
            })}
                </span>}
            </Link>
          </li>)}
      </ul>
      {step ? <Link to={`/lessons/play/${encodeURIComponent(step.lessonId)}`} className="btn btn-primary min-h-(--hit-min) w-full">
          <Play aria-hidden className="size-4" />
          {t('lessons-continue', {
        step: stepName(step)
      })}
        </Link> : <p className="bg-primary/10 text-primary rounded-xl p-3 text-center font-medium">
          {t('lessons-all-done-today')}
        </p>}
      {offer && next && <div className="flex gap-2">
          <button className="btn btn-primary btn-sm min-h-(--hit-min)" disabled={starting} onClick={() => start({
        variables: {
          input: {
            lessonSetId: next.id
          }
        },
        onCompleted: () => void navigate('/lessons')
      })}>
            {t('lessons-next-set')}
          </button>
          <button className="btn btn-ghost btn-sm min-h-(--hit-min)" onClick={() => setStayed(true)}>
            {t('lessons-stay')}
          </button>
        </div>}
    </section>;
}